import { action, makeObservable, observable } from 'mobx';
import { generateStoreHelpers } from '@bringg-frontend/bringg-web-infra';
import {
	OptimizationData,
	OptimizationInProgressRealTime,
	OptimizationResponseFailed,
	RouteOptimizationResult
} from '@bringg/dashboard-sdk/dist/RouteOptimization/RouteOptimization.consts';
import { PlannedRoute, Run, Task, User } from '@bringg/types';

import { ViewNames } from './consts';
import { OptimizationStatus } from './optimization-view/optimization-view';

export type UserAssignerRoot = UsersAssignerStore;

export const userAssignerRootStore = {
	getStore: () => UsersAssignerStore.instance,
	clearStore: () => UsersAssignerStore.clear()
};

export const { useStoreContext: useUserAssignerRootStore, withRootStore: withUserAssignerRootStore } =
	generateStoreHelpers(userAssignerRootStore);

class UsersAssignerStore {
	private static _instance?: UsersAssignerStore;
	userAssignerStore: UsersAssignerStore;

	public static get instance(): UsersAssignerStore {
		if (!UsersAssignerStore._instance) {
			UsersAssignerStore._instance = new UsersAssignerStore();
			UsersAssignerStore._instance.init();
		}

		return UsersAssignerStore._instance;
	}

	currentView: ViewNames = 'table';
	selectedDriver: User = null;
	selectedRun: Run = null;
	runParams: Partial<Run> = null;
	unstartedRunTasks: Task[] = [];
	teamPlannedRoutes: PlannedRoute[] = [];
	runsById: { [runId: number]: Run } = {};
	error = '';
	optimizationProgressPercentage = 0;
	optimizationResult: RouteOptimizationResult | OptimizationResponseFailed;
	optimizationStatus = OptimizationStatus.NONE;
	sentToOptimizationCount = { tasks: 0, resources: 0 };

	constructor() {
		makeObservable(this, {
			currentView: observable,
			setCurrentView: action,
			selectedDriver: observable,
			setSelectedDriver: action,
			selectedRun: observable,
			setSelectedRun: action,
			selectDriver: action,
			selectRun: action,
			runParams: observable,
			setRunParams: action,
			unstartedRunTasks: observable.shallow,
			setUnstartedRunTasks: action,
			teamPlannedRoutes: observable,
			setTeamPlannedRoutes: action,
			runsById: observable.shallow,
			setRunsById: action,
			error: observable,
			setError: action,
			optimizationProgressPercentage: observable,
			setOptimizationProgressPercentage: action,
			optimizationStatus: observable,
			sentToOptimizationCount: observable,
			optimizationResult: observable,
			setOptimizationResult: action,
			setSentToOptimizationCount: action,
			backFromOptimizationStep: action,
			updateOptimizationResults: action
		});
	}

	init() {
		this.userAssignerStore = new UsersAssignerStore();
	}

	static clear() {
		if (UsersAssignerStore._instance) {
			UsersAssignerStore._instance = undefined;
		}
	}

	setCurrentView = (view: ViewNames) => {
		this.currentView = view;
	};

	setSelectedDriver = (user: User) => {
		this.selectedDriver = user;
	};

	setSelectedRun = (run: Run) => {
		this.selectedRun = run;
	};

	selectDriver = driver => {
		this.selectedDriver = driver;
		this.selectedRun = null;
	};

	selectRun = (run: Run) => {
		this.selectedDriver = null;
		this.selectedRun = run;
	};

	setRunParams = (runParams: Partial<Run>) => {
		this.runParams = runParams;
	};

	setUnstartedRunTasks = (unstartedRunsTasks: Task[]) => {
		this.unstartedRunTasks = unstartedRunsTasks;
	};

	setTeamPlannedRoutes = (plannedRoute: PlannedRoute[]) => {
		this.teamPlannedRoutes = plannedRoute;
	};

	setRunsById = (runsById: { [runId: number]: Run }) => {
		this.runsById = runsById;
	};

	setError = (error: string) => {
		this.error = error;
	};

	setOptimizationProgressPercentage = (progressData: OptimizationInProgressRealTime) => {
		const finishPercentage = Math.round((progressData.iteration / progressData.totalIterations) * 100);
		this.optimizationProgressPercentage = finishPercentage;
	};

	setOptimizationResult = (results: RouteOptimizationResult | OptimizationResponseFailed) => {
		this.optimizationResult = results;
		this.optimizationStatus = (results as OptimizationResponseFailed).error
			? OptimizationStatus.FAILED
			: OptimizationStatus.DONE_SUCCESS;
	};

	updateOptimizationResults = (resultData: OptimizationData[]) => {
		(this.optimizationResult as RouteOptimizationResult).data = resultData;
	};

	setSentToOptimizationCount = (value: { tasks: number; resources: number }) => {
		this.sentToOptimizationCount = value;
	};

	backFromOptimizationStep = () => {
		this.optimizationProgressPercentage = 0;
		this.sentToOptimizationCount = { tasks: 0, resources: 0 };
		this.optimizationResult = undefined;
		this.optimizationStatus = OptimizationStatus.NONE;
	};
}

export default UsersAssignerStore;
